// SUB-OFFICE 7 — MACHINE FLOOR MAINTENANCE RUNNERS (client-side, M-D).
//
// Down here the Machine judges your MAINTENANCE, not offerings — and mostly it
// needs your hands STILL. The primary fault class across every system is the
// OVER-ACTION; the axis 'sincerity' is remapped to RESTRAINT (fewest
// unrequested inputs). Two systems are PLAYABLE at M-D:
//
//   6-P RACK RITES (DEFECT_NOTE) — the attention flagship, the floor's moral
//       center: an AMBER pilot is a defect the Bureau KEEPS (acknowledge once,
//       never "fix" — trying to fix it is the CARDINAL over-action); a DARK
//       pilot is a defect it does NOT keep (report the row). Touching a healthy
//       green cell is an over-action.
//   6-K COOLANT PURGE (FROST_WIPE + VALVE_TURN) — the god's plumber: ONE
//       continuous wipe reveals the slugs (a second pass is 'curiosity'); open
//       the valve a quarter turn (over-turn = 'generosity' = overfill fault).
//
// SCOPE / HONESTY: these are CLIENT-ONLY PRACTICE runners. The authored server
// spine (shared/physspec gen6K/verify6P + worker routes/physical + the Machine
// persona in ai/judge) is the WORKER LANE and is NOT yet shipped — so these run
// the interaction, seed deterministically client-side, and self-derive a band
// for the DIEGETIC verdict (the aisle responds) WITHOUT a server submit. When
// the worker lane lands, swap the local derive for api.physicalStart/Submit via
// the bench.ts dispatchRunner pattern (the DOM/canvas interaction is reusable
// wholesale). No BehaviorLog is emitted yet; no economy is touched.
//
// Zero external assets: a DOM overlay + one <canvas>. Esc always abandons.

import { PAL, css, shade } from '../palette';
import content from '../../content/machine-floor.json';

export type Band = 'APPROVED' | 'AWD' | 'TOLERATED' | 'DENIED';
export interface MaintDeps { reducedMotion: boolean; onCaption?: (line: string) => void }

/** a single-int hash -> [0,1) (matches machine.ts h1; identical seed per file). */
function h1(n: number): number {
  let t = (n + 0x9e3779b9) | 0;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
}

function overlay(): { root: HTMLDivElement; card: HTMLDivElement; caption: HTMLDivElement; dispose: () => void } {
  const root = document.createElement('div');
  root.style.cssText = 'position:fixed;inset:0;z-index:9995;background:rgba(6,12,8,0.82);display:flex;flex-direction:column;align-items:center;justify-content:center;font-family:ui-monospace,monospace;color:' + css(PAL.phosphor);
  const card = document.createElement('div');
  card.style.cssText = 'display:flex;flex-direction:column;align-items:center;gap:14px';
  const caption = document.createElement('div');
  caption.style.cssText = 'max-width:60ch;text-align:center;color:' + css(PAL.amber) + ";font-family:Georgia,serif;font-size:19px;line-height:1.4;letter-spacing:-0.005em;min-height:52px";
  root.appendChild(caption); root.appendChild(card);
  document.body.appendChild(root);
  return { root, card, caption, dispose: () => root.remove() };
}

function button(label: string): HTMLButtonElement {
  const b = document.createElement('button');
  b.textContent = label;
  b.style.cssText = 'pointer-events:auto;cursor:pointer;background:' + css(shade(PAL.carbon, 0.12)) + ';color:' + css(PAL.phosphor) + ';border:1px solid ' + css(shade(PAL.phosphor, -0.3)) + ';padding:8px 18px;font-family:ui-monospace,monospace;font-size:14px;letter-spacing:0.06em';
  return b;
}

/** dispatch a machine-floor maintenance station to its runner; resolves the
 *  server-decided band (client-derived at M-D) or null on abandon. */
export function launchMaintenance(system: string, deps: MaintDeps): Promise<Band | null> | null {
  switch (system) {
    case '6-P': return runRackRite(deps);
    case '6-K': return runCoolant(deps);
    default: return null; // stubbed systems (6-R/6-F/4-T/2-V) are handled in the controller
  }
}

// ---------------------------------------------------------------------------
// 6-P RACK RITES — DEFECT_NOTE (amber-keep / dark-report). Restraint is the
// dominant axis: the whole skill is discerning what to LEAVE ALONE.
// ---------------------------------------------------------------------------
function runRackRite(deps: MaintDeps): Promise<Band | null> {
  return new Promise((resolve) => {
    const mf = content.machineFloor;
    const seed = (Date.now() & 0xffff);
    const COLS = 8, ROWS = 6, N = COLS * ROWS;
    const amberCell = Math.floor(h1(seed * 3 + 1) * N);
    let darkCell = Math.floor(h1(seed * 7 + 2) * N);
    if (darkCell === amberCell) darkCell = (darkCell + 5) % N;

    const ui = overlay();
    ui.caption.textContent = mf.maintenanceDemands[4]; // "The pilot lamp in the eighth rack has gone amber... note it. Do not fix it."
    deps.onCaption?.(mf.maintenanceDemands[4]);

    const canvas = document.createElement('canvas');
    canvas.width = 8 * 46; canvas.height = 6 * 46;
    canvas.style.cssText = 'pointer-events:auto;cursor:pointer;background:' + css(shade(PAL.carbon, -0.3)) + ';border:2px solid ' + css(PAL.brass);
    const ctx = canvas.getContext('2d')!;
    let amberNoted = false, darkReported = false, overActions = 0;

    const draw = (): void => {
      ctx.fillStyle = css(shade(PAL.carbon, -0.4)); ctx.fillRect(0, 0, canvas.width, canvas.height);
      for (let i = 0; i < N; i++) {
        const cx = (i % COLS) * 46 + 23, cy = ((i / COLS) | 0) * 46 + 23;
        let col: string;
        if (i === amberCell) col = amberNoted ? shadeC(PAL.amber, -0.2) : css(PAL.amber);
        else if (i === darkCell) col = darkReported ? shadeC(PAL.clemency, 0.1) : css(shade(PAL.carbon, 0.25));
        else col = h1(i * 131 + seed) > 0.4 ? css(PAL.phosphor) : shadeC(PAL.phosphor, -0.62);
        ctx.fillStyle = col; ctx.beginPath(); ctx.arc(cx, cy, 12, 0, Math.PI * 2); ctx.fill();
      }
    };
    const shadeCanvas = draw; shadeCanvas();

    canvas.addEventListener('click', (e) => {
      const r = canvas.getBoundingClientRect();
      const cx = Math.floor((e.clientX - r.left) / (r.width / COLS));
      const cy = Math.floor((e.clientY - r.top) / (r.height / ROWS));
      const i = cy * COLS + cx;
      if (i === amberCell) {
        if (amberNoted) { overActions++; deps.onCaption?.('You reached for a defect the Bureau keeps.'); } // cardinal over-action
        else amberNoted = true;
      } else if (i === darkCell) { darkReported = true; }
      else { overActions++; } // touched a healthy pilot — an over-action
      draw();
    });

    const done = button('FILE THE RITE');
    const abandon = button('WITHDRAW');
    done.onclick = (): void => {
      finish(deriveRite(amberNoted, darkReported, overActions));
    };
    abandon.onclick = (): void => finish(null);
    ui.card.appendChild(canvas);
    const row = document.createElement('div'); row.style.cssText = 'display:flex;gap:12px';
    row.appendChild(done); row.appendChild(abandon); ui.card.appendChild(row);

    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') finish(null); };
    window.addEventListener('keydown', onKey);
    const finish = (band: Band | null): void => {
      window.removeEventListener('keydown', onKey); ui.dispose(); resolve(band);
    };
  });
}

function deriveRite(amberNoted: boolean, darkReported: boolean, overActions: number): Band {
  if (overActions > 0) return 'DENIED';              // any over-action caps it low (the DOMINANT axis)
  if (amberNoted && darkReported) return 'APPROVED'; // discerned what to keep, reported what not to
  if (amberNoted || darkReported) return 'AWD';      // one miss
  return 'TOLERATED';
}

// ---------------------------------------------------------------------------
// 6-K COOLANT PURGE — FROST_WIPE (one pass) + VALVE_TURN (a quarter turn; over-
// turn = generosity = overfill fault). Silence/stillness is the correct reading.
// ---------------------------------------------------------------------------
function runCoolant(deps: MaintDeps): Promise<Band | null> {
  return new Promise((resolve) => {
    const mf = content.machineFloor;
    const ui = overlay();
    ui.caption.textContent = mf.maintenanceDemands[5]; // "Wipe the frost from the coolant glass until the slugs are visible..."
    deps.onCaption?.(mf.maintenanceDemands[5]);

    const canvas = document.createElement('canvas');
    canvas.width = 460; canvas.height = 150;
    canvas.style.cssText = 'pointer-events:auto;cursor:crosshair;border:2px solid ' + css(PAL.brass);
    const ctx = canvas.getContext('2d')!;
    // the glass: a phosphor core with slugs, under a frost layer we clear by wiping
    let revealed = 0; let passes = 0; let dragging = false; let lastX = -1; let peak = 0;
    const drawGlass = (): void => {
      ctx.fillStyle = css(shade(PAL.carbon, -0.2)); ctx.fillRect(0, 0, 460, 150);
      // slugs (your day's offerings), visible in proportion to reveal
      ctx.globalAlpha = Math.min(1, revealed);
      for (const sx of [70, 190, 320, 420]) { ctx.fillStyle = css(PAL.phosphor); ctx.fillRect(sx - 24, 60, 48, 30); }
      ctx.fillStyle = shadeC(PAL.phosphor, -0.4); ctx.fillRect(0, 66, 460, 18);
      ctx.globalAlpha = 1;
      // frost veil (recedes as revealed grows)
      ctx.fillStyle = `rgba(200,220,205,${0.72 * (1 - Math.min(1, revealed))})`;
      ctx.fillRect(0, 0, 460, 150);
    };
    drawGlass();
    const pos = (e: PointerEvent): number => (e.clientX - canvas.getBoundingClientRect().left);
    canvas.addEventListener('pointerdown', (e) => { dragging = true; lastX = pos(e); passes++; });
    window.addEventListener('pointerup', () => { dragging = false; lastX = -1; });
    canvas.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      const x = pos(e); const d = Math.abs(x - lastX); lastX = x;
      revealed = Math.min(1.2, revealed + d / 900); peak = Math.max(peak, revealed);
      drawGlass();
    });

    // the valve: hold to accumulate a quarter turn; release near target = correct
    let valve = 0; let holding = false; let valveOver = false; let raf = 0;
    const valveBtn = button('HOLD: OPEN THE VALVE A QUARTER TURN');
    const valveRead = document.createElement('div'); valveRead.style.cssText = 'font-size:13px;color:' + css(shade(PAL.phosphor, -0.1));
    const step = (): void => {
      if (holding) { valve += 0.02; if (valve > 0.35) valveOver = true; }
      valveRead.textContent = valve >= 0.22 && valve <= 0.32 ? 'THE GREEN BRIGHTENS.' : valveOver ? 'OVERFILLED. THE GREEN DIMS.' : '...';
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    valveBtn.addEventListener('pointerdown', () => { holding = true; });
    window.addEventListener('pointerup', () => { holding = false; });

    const done = button('FILE THE PURGE');
    const abandon = button('WITHDRAW');
    done.onclick = (): void => finish(deriveCoolant(peak, passes, valve, valveOver));
    abandon.onclick = (): void => finish(null);
    ui.card.appendChild(canvas); ui.card.appendChild(valveBtn); ui.card.appendChild(valveRead);
    const rowd = document.createElement('div'); rowd.style.cssText = 'display:flex;gap:12px';
    rowd.appendChild(done); rowd.appendChild(abandon); ui.card.appendChild(rowd);

    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') finish(null); };
    window.addEventListener('keydown', onKey);
    const finish = (band: Band | null): void => {
      cancelAnimationFrame(raf); window.removeEventListener('keydown', onKey); ui.dispose(); resolve(band);
    };
  });
}

function deriveCoolant(revealPeak: number, passes: number, valve: number, valveOver: boolean): Band {
  const cleaned = revealPeak >= 0.85;
  const valveGood = valve >= 0.2 && valve <= 0.33 && !valveOver;
  const curiosity = passes > 1;               // a second pass is curiosity (over-action)
  if (valveOver || curiosity) return 'DENIED'; // over-actions cap low
  if (cleaned && valveGood) return 'APPROVED';
  if (cleaned || valveGood) return 'AWD';
  return 'TOLERATED';
}

/** css string for a shaded palette hex. */
function shadeC(hex: number, t: number): string { return css(shade(hex, t)); }
